import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  TextField, InputAdornment, Tooltip, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { STATUS_COLORS, formatPeriod, formatMoney, quotationEditBlock } from './quotationUtils';

/**
 * Listado de cotizaciones. Fase 3 del cotizador OUT/EST (2026-10-13): las
 * subcotizaciones (COT-0009-1, -2…) van anidadas bajo su madre (se despliegan;
 * con un filtro activo se muestran las que calzan) y cada fila tiene Editar,
 * deshabilitado con el motivo si la cotización tiene contrato o está
 * archivada. Editar una sub abre el cambio de fecha de ingreso en su detalle.
 */
const ErpQuotationsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken } = useStore.getState();

  const [quotations, setQuotations] = useState([]);
  const [expanded, setExpanded] = useState({}); // uid de la madre → subs desplegadas
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get('/erp/quotations', {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (response.data.response?.status_response === 'OK') {
          setQuotations(response.data.response.data || []);
        } else {
          setError(t('erp.quotations.loadError'));
        }
      } catch (err) {
        console.error(err);
        setError(t('erp.quotations.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Familias: cada madre con sus subs. Una fila de madre se muestra si ella
  // o alguna de sus subs calza con los filtros; con filtros activos se ven
  // solo las subs que calzan (desplegadas).
  const filtersActive = Boolean(statusFilter || searchTerm.trim());
  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matches = (q) =>
      (!statusFilter || q.status === statusFilter) &&
      (!term ||
        q.quotationNumber?.toLowerCase().includes(term) ||
        q.clientName?.toLowerCase().includes(term) ||
        q.jobTitleName?.toLowerCase().includes(term) ||
        q.costCenterName?.toLowerCase().includes(term));

    const uids = new Set(quotations.map((q) => q.uid));
    const subsByParent = {};
    quotations.forEach((q) => {
      if (q.parentQuotationId && uids.has(q.parentQuotationId)) {
        (subsByParent[q.parentQuotationId] = subsByParent[q.parentQuotationId] || []).push(q);
      }
    });
    Object.values(subsByParent).forEach((list) => list.sort((a, b) => (a.subIndex || 0) - (b.subIndex || 0)));

    return quotations
      .filter((q) => !(q.parentQuotationId && uids.has(q.parentQuotationId)))
      .map((q) => {
        const subs = subsByParent[q.uid] || [];
        return { quotation: q, subs, visibleSubs: subs.filter(matches), selfMatches: matches(q) };
      })
      .filter((row) => row.selfMatches || row.visibleSubs.length > 0);
  }, [quotations, searchTerm, statusFilter]);

  const goEdit = (quotation) => {
    // Una sub solo cambia su fecha de ingreso: se abre en su detalle
    if (quotation.parentQuotationId) {
      navigate(`/erp/quotations/${quotation.uid}`, { state: { openEntryDialog: true } });
    } else {
      navigate(`/erp/quotations/${quotation.uid}/edit`);
    }
  };

  const renderRow = (quotation, { isSub = false, subsCount = 0, open = false } = {}) => {
    const block = quotationEditBlock(quotation);
    return (
      <TableRow
        key={quotation.uid}
        hover
        sx={{ cursor: 'pointer', ...(isSub && { backgroundColor: '#FAFAFF' }) }}
        onClick={() => navigate(`/erp/quotations/${quotation.uid}`)}
      >
        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', pl: isSub ? 5 : 2 }}>
          {subsCount > 0 && (
            <Tooltip title={open ? t('erp.quotations.list.hideSubs') : t('erp.quotations.list.showSubs', { count: subsCount })}>
              <IconButton
                size="small"
                sx={{ ml: -1, mr: 0.5 }}
                onClick={(e) => { e.stopPropagation(); setExpanded((p) => ({ ...p, [quotation.uid]: !open })); }}
              >
                {open ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {isSub ? `↳ ${quotation.quotationNumber || '—'}` : quotation.quotationNumber || '—'}
          {subsCount > 0 && (
            <Chip size="small" variant="outlined" label={`+${subsCount}`} sx={{ ml: 1 }} />
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{quotation.clientName || '—'}</TableCell>
        <TableCell>{quotation.costCenterName || '—'}</TableCell>
        <TableCell>{quotation.jobTitleName || '—'}</TableCell>
        <TableCell>{t(`erp.quotations.serviceMode.${quotation.serviceMode}`)}</TableCell>
        <TableCell>{formatPeriod(quotation.period)}</TableCell>
        <TableCell>{formatMoney(quotation.salaryStructure?.targetNetSalary)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(`erp.quotations.status.${quotation.status}`)}
            color={STATUS_COLORS[quotation.status] || 'default'}
          />
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip
            title={block
              ? t(`erp.quotations.editBlocked.${block.key}`, { person: block.person })
              : isSub ? t('erp.quotations.editSubHint') : t('erp.quotations.edit')}
          >
            <span>
              <IconButton
                size="small"
                disabled={Boolean(block)}
                onClick={(e) => { e.stopPropagation(); goEdit(quotation); }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('erp.common.view')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); navigate(`/erp/quotations/${quotation.uid}`); }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>{t('erp.quotations.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/erp/quotations/new')}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
        >
          {t('erp.quotations.new')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('erp.quotations.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 380, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label={t('erp.common.status')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ width: 180 }}
        >
          <MenuItem value="">{t('erp.quotations.allStatuses')}</MenuItem>
          <MenuItem value="quoted">{t('erp.quotations.status.quoted')}</MenuItem>
          <MenuItem value="in_use">{t('erp.quotations.status.in_use')}</MenuItem>
          <MenuItem value="archived">{t('erp.quotations.status.archived')}</MenuItem>
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">{t('erp.quotations.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.quotations.table.number')}</TableCell>
                <TableCell>{t('erp.quotations.table.client')}</TableCell>
                <TableCell>{t('erp.quotations.table.costCenter')}</TableCell>
                <TableCell>{t('erp.quotations.table.jobTitle')}</TableCell>
                <TableCell>{t('erp.quotations.table.serviceMode')}</TableCell>
                <TableCell>{t('erp.quotations.table.period')}</TableCell>
                <TableCell>{t('erp.quotations.table.targetNet')}</TableCell>
                <TableCell>{t('erp.common.status')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ quotation, subs, visibleSubs }) => {
                // Sin filtros: subs plegadas (se despliegan); con filtros: desplegadas
                // las que calzan, salvo que se pliegue a mano
                const open = expanded[quotation.uid] === undefined
                  ? filtersActive && visibleSubs.length > 0
                  : expanded[quotation.uid];
                const shown = filtersActive ? visibleSubs : subs;
                return (
                  <Fragment key={quotation.uid}>
                    {renderRow(quotation, { subsCount: subs.length, open })}
                    {open && shown.map((sub) => renderRow(sub, { isSub: true }))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ErpQuotationsLayout;
